import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Alert, Chip, Tabs, Tab,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, LinearProgress, Tooltip, IconButton, TextField,
  InputAdornment, Select, MenuItem, FormControl, InputLabel, Divider,
  Drawer, Stack
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as ResolvedIcon,
  RadioButtonUnchecked as OpenIcon,
  Warning as WarningIcon,
  AccessibilityNew as A11yIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';

// ── Per-version theme palette — navy/blue family matching Reliability page ─────
const PRIMARY = '#0f1f3d';
const NAVY2   = '#1a3460';
const SURFACE = '#f6f8fb';

const VERSION_THEME = {
  '2.0': {
    accent:   '#1d6fd4',
    light:    '#e8f1fb',
    mid:      '#5a9fd4',
    dark:     PRIMARY,
    label:    'WCAG 2.0',
    gradient: `linear-gradient(105deg, ${PRIMARY} 0%, ${NAVY2} 100%)`,
  },
  '2.1': {
    accent:   '#155fa0',
    light:    '#dbeafe',
    mid:      '#4e89c8',
    dark:     '#0d2147',
    label:    'WCAG 2.1',
    gradient: 'linear-gradient(105deg, #0d2147 0%, #155fa0 100%)',
  },
  '2.2': {
    accent:   '#2563eb',
    light:    '#eff6ff',
    mid:      '#60a5fa',
    dark:     '#1e40af',
    label:    'WCAG 2.2',
    gradient: 'linear-gradient(105deg, #1e40af 0%, #2563eb 100%)',
  },
};

const CLOSED_STATES   = ['resolved', 'closed', 'done', 'completed', 'removed'];
const isOpen = (s) => !CLOSED_STATES.includes((s || '').toLowerCase());

const WCAG_PRIORITY_META = {
  'A0': { label: 'A0 – Critical',    color: '#7c3aed', bg: '#f5f3ff' },
  'A1': { label: 'A1 – High',        color: '#dc2626', bg: '#fef2f2' },
  'A2': { label: 'A2 – Medium',      color: '#ea580c', bg: '#fff7ed' },
  'A3': { label: 'A3 – Low',         color: '#ca8a04', bg: '#fefce8' },
  'A4': { label: 'A4 – Best Effort', color: '#16a34a', bg: '#f0fdf4' },
};

const ADO_BASE = 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit';

const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysSince = (d) => d ? Math.round((Date.now() - new Date(d)) / 86400000) : null;
const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, pct, color, bg, icon: Icon }) => (
  <Paper elevation={0} sx={{
    p: 2,
    border: `1px solid ${color}25`,
    borderTop: `3px solid ${color}`,
    bgcolor: bg || SURFACE,
    borderRadius: 2,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</Typography>
          {pct !== undefined && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, color, opacity: 0.7, lineHeight: 1 }}>{pct}%</Typography>
          )}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        {sub && <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: 10 }}>{sub}</Typography>}
      </Box>
      {Icon && <Icon sx={{ fontSize: 28, color: `${color}50` }} />}
    </Box>
  </Paper>
);

// ── State bar ─────────────────────────────────────────────────────────────────
const STATE_COLOR = {
  New: '#6366f1', Active: '#f59e0b', 'In Progress': '#f59e0b',
  Resolved: '#22c55e', Closed: '#94a3b8', Done: '#22c55e',
  Completed: '#22c55e', 'On Hold': '#a855f7', Removed: '#cbd5e1',
  'Pending Validation': '#0891b2',
};
const getStateColor = (s) => STATE_COLOR[s] || '#64748b';

const StateBar = ({ byState }) => {
  const entries = Object.entries(byState || {}).sort((a, b) => b[1] - a[1]);
  const total   = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return <Typography variant="caption" color="text.secondary">No data</Typography>;
  return (
    <Box>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mb: 1 }}>
        {entries.map(([state, count]) => (
          <Tooltip key={state} title={`${state}: ${count}`} arrow>
            <Box sx={{ width: `${pct(count, total)}%`, bgcolor: getStateColor(state), transition: 'width 0.3s' }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {entries.map(([state, count]) => (
          <Box key={state} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStateColor(state), flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
              {state} <strong>{count}</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── SLA gauge ─────────────────────────────────────────────────────────────────
const SlaRow = ({ label, value, color, total }) => (
  <Box sx={{ mb: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color }}>{value}</Typography>
    </Box>
    <LinearProgress variant="determinate" value={pct(value, total)}
      sx={{ height: 5, borderRadius: 3, bgcolor: `${color}18`,
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
  </Box>
);

// ── Issue detail drawer ───────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
    <Typography variant="caption" sx={{ width: 100, flexShrink: 0, color: 'text.secondary', fontWeight: 600, pt: 0.2 }}>{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const IssueDrawer = ({ issue, open, onClose, theme }) => {
  if (!issue) return null;
  const age = isOpen(issue.state) ? daysSince(issue.createdDate) : null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 0 } }}>
      <Box sx={{ px: 2.5, py: 2, background: theme.gradient, color: '#fff', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>#{issue.adoId}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}>{issue.title}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff', flexShrink: 0, mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: SURFACE, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={issue.state || '?'} size="small"
          sx={{ bgcolor: getStateColor(issue.state), color: '#fff', fontWeight: 700, fontSize: 10, height: 20 }} />
        {(issue.asdA11yPriority || issue.priority) && (
          <Chip label={issue.asdA11yPriority || `P${issue.priority}`} size="small"
            sx={{ bgcolor: WCAG_PRIORITY_META[(issue.asdA11yPriority || '').toUpperCase()]?.color || '#64748b', color: '#fff', fontWeight: 700, fontSize: 10, height: 20 }} />
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Open in ADO" arrow>
            <IconButton size="small" component="a" href={`${ADO_BASE}/${issue.adoId}`} target="_blank" rel="noopener">
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ px: 2.5, py: 2, overflowY: 'auto' }}>
        <Stack spacing={0.25}>
          <DetailRow label="Assigned To"   value={issue.assignedTo} />
          <DetailRow label="Area Path"     value={issue.areaPath} />
          <DetailRow label="WCAG Criterion" value={issue.wcagCriterion} />
          <DetailRow label="Report Class"  value={issue.asdA11yComplianceReport || issue.complianceReportClassification} />
          <DetailRow label="Created"       value={fmtDate(issue.createdDate)} />
          <DetailRow label="Last Updated"  value={fmtDate(issue.changedDate)} />
          <DetailRow label="Closed"        value={fmtDate(issue.closedDate)} />
          {age !== null && (
            <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
              <Typography variant="caption" sx={{ width: 100, flexShrink: 0, color: 'text.secondary', fontWeight: 600, pt: 0.2 }}>Age (open)</Typography>
              <Typography variant="body2" sx={{ color: age > 90 ? '#dc2626' : age > 30 ? '#ea580c' : 'inherit', fontWeight: age > 30 ? 700 : 400 }}>
                {age} days
              </Typography>
            </Box>
          )}
        </Stack>
        {issue.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</Typography>
            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
              {issue.description}
            </Typography>
          </>
        )}
      </Box>
    </Drawer>
  );
};

// ── Per-version panel ─────────────────────────────────────────────────────────
const VersionPanel = ({ version, stats, lastSync }) => {
  const theme = VERSION_THEME[version];

  const [issues, setIssues]             = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError]   = useState(null);
  const [search, setSearch]             = useState('');
  const [filterState, setFilterState]   = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [openOnly, setOpenOnly]         = useState(true);
  const [orderBy, setOrderBy]           = useState('createdDate');
  const [order, setOrder]               = useState('desc');
  const [drawerIssue, setDrawerIssue]   = useState(null);
  const [drawerOpen, setDrawerOpen]     = useState(false);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        setIssuesLoading(true);
        setIssuesError(null);
        const r = await get(`/api/admin/accessibility/issues?wcagVersion=${version}&page=1&limit=1000`);
        if (r.success) setIssues(r.issues || []);
        else setIssuesError(r.message || 'Failed to load issues');
      } catch (e) {
        setIssuesError(e.message || 'Failed to load issues');
      } finally {
        setIssuesLoading(false);
      }
    };
    fetchIssues();
  }, [version]);

  // ── Derived metrics ──────────────────────────────────────────────────────
  const vStats = stats?.byWcagVersion?.[version] || { total: 0, active: 0, resolved: 0 };
  const byState = stats?.byStateByVersion?.[version] || {};
  const sla = version === '2.0' ? (stats?.slaByVersion?.['2.0'] || stats?.sla) : null;

  const pendingValidation = byState['Pending Validation'] || 0;
  const openCount = Math.max(0, (vStats.active || 0) - pendingValidation);

  const priorities = useMemo(() => {
    const counts = { 'A0': 0, 'A1': 0, 'A2': 0, 'A3': 0, 'A4': 0 };
    issues
      .filter(i => isOpen(i.state) && (i.state || '').toLowerCase() !== 'pending validation')
      .forEach(i => {
        const p = (i.asdA11yPriority || '').toUpperCase();
        if (Object.prototype.hasOwnProperty.call(counts, p)) counts[p]++;
      });
    return counts;
  }, [issues]);

  const assigneeStats = useMemo(() => {
    const map = {};
    issues.filter(i => isOpen(i.state)).forEach(i => {
      const name = i.assignedTo || 'Unassigned';
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [issues]);

  const stateOptions    = useMemo(() => [...new Set(issues.map(i => i.state).filter(Boolean))].sort(), [issues]);

  // ── Filtered table ───────────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let rows = [...issues];
    if (openOnly)       rows = rows.filter(i => isOpen(i.state));
    if (filterState)    rows = rows.filter(i => i.state === filterState);
    if (filterPriority) rows = rows.filter(i => (i.asdA11yPriority || '').toUpperCase() === filterPriority);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(i =>
        String(i.adoId || '').includes(q) ||
        (i.title || '').toLowerCase().includes(q) ||
        (i.assignedTo || '').toLowerCase().includes(q)
      );
    }
    return rows.sort((a, b) => {
      let va = a[orderBy], vb = b[orderBy];
      if (['createdDate', 'closedDate', 'changedDate'].includes(orderBy)) {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else {
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();
      }
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [issues, openOnly, filterState, filterPriority, search, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('asc'); }
  };

  const SortCell = ({ col, label, sx: cellSx }) => (
    <TableCell sx={{ fontWeight: 700, bgcolor: SURFACE, py: 1, whiteSpace: 'nowrap', ...cellSx }}>
      <TableSortLabel active={orderBy === col} direction={orderBy === col ? order : 'asc'} onClick={() => handleSort(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const totalSla = sla ? (sla.overdue + sla.approachingDue + sla.withinSla + (sla.noSla || 0)) : 0;

  return (
    <Box>
      {/* ── Hero header ── */}
      <Box sx={{ background: theme.gradient, borderRadius: 2, px: 3, py: 2.5, mb: 3, color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <A11yIcon sx={{ fontSize: 28, opacity: 0.85 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>{theme.label}</Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.75, fontSize: 12 }}>
          Accessibility compliance tracking · {lastSync ? `Last synced ${fmtDate(lastSync)}` : 'Sync pending'}
        </Typography>
      </Box>

      {/* ── KPI row ── */}
      {(() => {
        const tot = vStats.total || 0;
        const pctOf = (n) => tot > 0 ? Math.round(((n ?? 0) / tot) * 100) : 0;
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            <KpiCard label="Total" value={tot} sub="All time" color={theme.accent} bg={theme.light} icon={A11yIcon} />
            <KpiCard label="Open" value={openCount} pct={pctOf(openCount)} sub="Excl. pending validation" color="#475569" bg={SURFACE} icon={OpenIcon} />
            <KpiCard label="Pending Validation" value={pendingValidation} pct={pctOf(pendingValidation)} sub="Awaiting sign-off" color="#0891b2" bg="#ecfeff" icon={ScheduleIcon} />
            <KpiCard label="Resolved" value={vStats.resolved} pct={pctOf(vStats.resolved)} sub="Closed · Done" color="#16a34a" bg="#f0fdf4" icon={ResolvedIcon} />
            <KpiCard
              label={version === '2.0' ? 'SLA Overdue' : 'No SLA'}
              value={version === '2.0' ? (sla?.overdue ?? '—') : 'N/A'}
              pct={version === '2.0' && sla?.overdue != null ? pctOf(sla.overdue) : undefined}
              sub={version === '2.0' ? 'Breached deadline' : 'SLA only for 2.0'}
              color={version === '2.0' && sla?.overdue > 0 ? '#dc2626' : '#64748b'}
              bg={version === '2.0' && sla?.overdue > 0 ? '#fef2f2' : SURFACE}
              icon={version === '2.0' && sla?.overdue > 0 ? ErrorIcon : ScheduleIcon}
            />
          </Box>
        );
      })()}

      {/* ── Three-column metrics row (CSS grid avoids MUI negative-margin overlap) ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        {/* State breakdown */}
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.dark }}>State Breakdown</Typography>
          <StateBar byState={byState} />
        </Paper>

        {/* WCAG Priority breakdown */}
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.dark }}>Open by WCAG Priority</Typography>
          {(() => {
            const total = Object.values(priorities).reduce((s, v) => s + v, 0);
            return ['A0', 'A1', 'A2', 'A3', 'A4'].map(p => (
              <SlaRow key={p} label={WCAG_PRIORITY_META[p].label} value={priorities[p]} color={WCAG_PRIORITY_META[p].color} total={total} />
            ));
          })()}
        </Paper>

        {/* SLA (2.0) or Top Assignees (2.1 / 2.2) */}
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          {version === '2.0' && sla ? (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.dark }}>SLA Status</Typography>
              <SlaRow label="Overdue"     value={sla.overdue || 0}        color="#dc2626" total={totalSla} />
              <SlaRow label="Approaching" value={sla.approachingDue || 0} color="#ea580c" total={totalSla} />
              <SlaRow label="Within SLA"  value={sla.withinSla || 0}      color="#16a34a" total={totalSla} />
              <SlaRow label="No SLA set"  value={sla.noSla || 0}          color="#94a3b8" total={totalSla} />
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.dark }}>Top Assignees (open)</Typography>
              {assigneeStats.length === 0
                ? <Typography variant="caption" color="text.secondary">No open issues</Typography>
                : assigneeStats.map(([name, count]) => (
                  <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Typography variant="caption" sx={{ flex: 1, fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name.replace(/\s*<[^>]+>/, '').trim()}
                    </Typography>
                    <Chip label={count} size="small"
                      sx={{ height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: count >= 5 ? '#fef2f2' : count >= 3 ? '#fff7ed' : '#f0fdf4',
                        color:   count >= 5 ? '#dc2626' : count >= 3 ? '#ea580c' : '#16a34a' }} />
                  </Box>
                ))
              }
            </>
          )}
        </Paper>
      </Box>

      {/* ── Top assignees row for WCAG 2.0 (sits below SLA, full width) ── */}
      {version === '2.0' && assigneeStats.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.dark }}>Top Assignees (open)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)' }, gap: 1 }}>
            {assigneeStats.map(([name, count]) => (
              <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ flex: 1, fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name.replace(/\s*<[^>]+>/, '').trim()}
                </Typography>
                <Chip label={count} size="small"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0,
                    bgcolor: count >= 5 ? '#fef2f2' : count >= 3 ? '#fff7ed' : '#f0fdf4',
                    color:   count >= 5 ? '#dc2626' : count >= 3 ? '#ea580c' : '#16a34a' }} />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Issues table ── */}
      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
        {/* Table header / filters */}
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.dark, flex: 1, minWidth: 120 }}>
            Issues
            <Chip label={displayed.length} size="small"
              sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700,
                bgcolor: theme.light, color: theme.accent }} />
          </Typography>

          {/* Open-only toggle */}
          <Chip
            label={openOnly ? 'Open only' : 'All issues'}
            size="small"
            onClick={() => setOpenOnly(v => !v)}
            sx={{
              fontWeight: 700, fontSize: 11, height: 26, cursor: 'pointer',
              bgcolor: openOnly ? theme.accent : '#f1f5f9',
              color:   openOnly ? '#fff' : 'text.secondary',
              '&:hover': { opacity: 0.85 },
            }}
          />

          {/* State filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: 12 }}>State</InputLabel>
            <Select value={filterState} label="State" onChange={e => setFilterState(e.target.value)}
              sx={{ fontSize: 12, height: 32 }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {stateOptions.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Priority filter */}
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: 12 }}>Priority</InputLabel>
            <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}
              sx={{ fontSize: 12, height: 32 }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {['A0', 'A1', 'A2', 'A3', 'A4'].map(p => <MenuItem key={p} value={p} sx={{ fontSize: 12 }}>{p}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Search */}
          <TextField size="small" placeholder="Search ID · title · assignee…" value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 13 }} /></IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: 12, height: 32 },
            }}
            sx={{ width: 220 }}
          />
        </Box>

        {issuesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
        ) : issuesError ? (
          <Alert severity="error" sx={{ m: 2 }}>{issuesError}</Alert>
        ) : displayed.length === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'center', py: 5 }}>
            No issues match the selected filters.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <SortCell col="adoId"       label="ID" />
                  <SortCell col="title"        label="Title" />
                  <SortCell col="state"        label="State" />
                  <SortCell col="asdA11yPriority" label="Priority" />
                  <SortCell col="assignedTo"   label="Assigned To" />
                  <SortCell col="createdDate"  label="Created" />
                  <SortCell col="changedDate"  label="Updated" />
                  <TableCell sx={{ fontWeight: 700, bgcolor: SURFACE, py: 1 }}>Age</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map(issue => {
                  const open = isOpen(issue.state);
                  const age  = open ? daysSince(issue.createdDate) : null;
                  return (
                    <TableRow key={issue._id || issue.adoId} hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: `${theme.accent}08` } }}
                      onClick={() => { setDrawerIssue(issue); setDrawerOpen(true); }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: 12, color: theme.accent, fontWeight: 600 }}>{issue.adoId}</Typography>
                          <IconButton size="small" component="a" href={`${ADO_BASE}/${issue.adoId}`} target="_blank" rel="noopener"
                            sx={{ p: 0.25 }}>
                            <OpenInNewIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Tooltip title={issue.title} placement="top-start">
                          <Typography variant="body2" sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                            {issue.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={issue.state || '—'} size="small"
                          sx={{ fontSize: 10, height: 20, bgcolor: getStateColor(issue.state), color: '#fff', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        {(issue.asdA11yPriority || issue.priority)
                          ? <Chip label={issue.asdA11yPriority || `P${issue.priority}`} size="small"
                              sx={{ fontSize: 10, height: 20, bgcolor: WCAG_PRIORITY_META[(issue.asdA11yPriority || '').toUpperCase()]?.color || '#64748b', color: '#fff', fontWeight: 700 }} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {(issue.assignedTo || 'Unassigned').replace(/\s*<[^>]+>/, '').trim()}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(issue.createdDate)}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(issue.changedDate)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {age !== null
                          ? <Typography variant="caption"
                              sx={{ fontWeight: age > 90 ? 700 : 400, color: age > 90 ? '#dc2626' : age > 30 ? '#ea580c' : 'text.secondary' }}>
                              {age}d
                            </Typography>
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <IssueDrawer issue={drawerIssue} open={drawerOpen} onClose={() => setDrawerOpen(false)} theme={theme} />
    </Box>
  );
};

// ── Root component ────────────────────────────────────────────────────────────
const AccessibilityTab = () => {
  const [activeTab, setActiveTab]   = useState(0);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const VERSIONS = ['2.0', '2.1', '2.2'];

  const fetchStats = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const r = await get('/api/admin/accessibility/stats?days=365');
      if (r.success) setStats(r.stats);
      else setError(r.message || 'Failed to load stats');
    } catch (e) {
      setError(e.message || 'Failed to load stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
        <CircularProgress sx={{ color: '#1d6fd4' }} />
        <Typography variant="body2" color="text.secondary">Loading accessibility data…</Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  return (
    <Box sx={{ width: '100%', bgcolor: '#edf1f7', borderRadius: 2, p: 0 }}>
      {/* ── Page header — navy banner matching Reliability page ── */}
      <Box sx={{
        background: `linear-gradient(105deg, ${PRIMARY} 0%, ${NAVY2} 100%)`,
        borderRadius: '12px 12px 0 0',
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <A11yIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 720, fontSize: 20, letterSpacing: '-0.3px', color: '#fff', lineHeight: 1.2 }}>
              Accessibility Compliance
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#cfe0eb', mt: 0.3 }}>
              WCAG issue tracking across all versions · {stats?.lastSync ? `Last sync ${fmtDate(stats.lastSync)}` : 'No sync data'}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh stats" arrow>
          <IconButton onClick={() => fetchStats(true)} disabled={refreshing}
            sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: 1.5, color: '#fff' }}>
            <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Content area with padding ── */}
      <Box sx={{ p: 2 }}>

      {/* ── Version summary strip ── */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {VERSIONS.map((v, i) => {
          const th   = VERSION_THEME[v];
          const vs   = stats?.byWcagVersion?.[v] || { total: 0, active: 0, resolved: 0 };
          const isSelected = activeTab === i;
          return (
            <Grid item xs={12} sm={4} key={v}>
              <Paper elevation={0} onClick={() => setActiveTab(i)}
                sx={{
                  p: 1.75, border: `2px solid ${isSelected ? th.accent : '#e2e8f0'}`,
                  borderRadius: 2, cursor: 'pointer', transition: 'all 0.15s',
                  bgcolor: isSelected ? th.light : '#fff',
                  '&:hover': { borderColor: th.mid, bgcolor: th.light },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, color: th.accent }}>{th.label}</Typography>
                  <Chip label={`${vs.active} open`} size="small"
                    sx={{ fontSize: 10, height: 18, fontWeight: 700,
                      bgcolor: vs.active > 0 ? '#fef2f2' : SURFACE,
                      color:   vs.active > 0 ? '#dc2626' : '#475569' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 22, color: th.accent, lineHeight: 1 }}>{vs.total}</Typography>
                    <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', textTransform: 'uppercase' }}>Total</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 22, color: '#475569', lineHeight: 1 }}>{vs.resolved}</Typography>
                    <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', textTransform: 'uppercase' }}>Resolved</Typography>
                  </Box>
                </Box>
                {/* Progress bar */}
                <Box sx={{ mt: 1 }}>
                  <LinearProgress variant="determinate" value={pct(vs.resolved, vs.total)}
                    sx={{ height: 4, borderRadius: 2, bgcolor: `${th.accent}20`,
                      '& .MuiLinearProgress-bar': { bgcolor: th.accent, borderRadius: 2 } }} />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* ── WCAG version tabs ── */}
      <Paper elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe5ee', mb: 3, borderRadius: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 1.5,
            '& .MuiTab-root': { fontWeight: 600, fontSize: 13.5, textTransform: 'none', minHeight: 46 },
            '& .MuiTabs-indicator': {
              bgcolor: VERSION_THEME[VERSIONS[activeTab]]?.accent,
              height: 2.5, borderRadius: '2.5px 2.5px 0 0'
            },
            '& .Mui-selected': { color: `${VERSION_THEME[VERSIONS[activeTab]]?.accent} !important` },
          }}
        >
          {VERSIONS.map((v, i) => {
            const th = VERSION_THEME[v];
            const vs = stats?.byWcagVersion?.[v] || { active: 0 };
            return (
              <Tab key={v} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {th.label}
                  {vs.active > 0 && (
                    <Chip label={vs.active} size="small"
                      sx={{ height: 16, fontSize: 9, fontWeight: 700,
                        bgcolor: activeTab === i ? th.light : '#f1f5f9',
                        color:   activeTab === i ? th.accent : 'text.secondary' }} />
                  )}
                </Box>
              } />
            );
          })}
        </Tabs>
      </Paper>

      {/* ── Active version panel ── */}
      <VersionPanel
        key={VERSIONS[activeTab]}
        version={VERSIONS[activeTab]}
        stats={stats}
        lastSync={stats?.lastSync}
      />

      </Box>{/* end content padding Box */}
    </Box>
  );
};

export default AccessibilityTab;
